import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { UiButton } from '@shared/components/ui-button/ui-button';
import { UpdateBlockerReason } from '@shared/services/update/busy-state';
import { UpdateManagerService } from '@shared/services/update/update-manager';

@Component({
  selector: 'app-update-banner',
  standalone: true,
  imports: [UiButton],
  templateUrl: './update-banner.html',
  styleUrl: './update-banner.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateBanner {
  private static readonly CHECKIN_BLOCKING_REASON: UpdateBlockerReason = 'checkin';
  private static readonly BLOCKING_REASON_LABELS: Record<UpdateBlockerReason, string> = {
    checkin: 'hay un check-in activo',
    scan: 'hay un escaneo QR en curso',
    camera: 'la camara esta en uso',
    'local-save': 'se estan guardando datos localmente',
    'checkout-sync': 'se esta enviando o sincronizando informacion',
    'draft-remito': 'hay un remito en curso sin cerrar',
    'draft-photo': 'hay una foto capturada sin confirmar'
  };

  protected readonly updateManager = inject(UpdateManagerService);
  protected readonly canApply = this.updateManager.canApply;
  protected readonly title = computed(() => {
    switch (this.updateManager.status()) {
      case 'failed':
        return 'No se pudo preparar la actualizacion.';
      case 'unrecoverable':
        return 'La aplicacion necesita una recarga completa.';
      default:
        return 'Nueva version disponible.';
    }
  });
  protected readonly description = computed(() => {
    const status = this.updateManager.status();
    const reasons = this.updateManager.blockingReasons();

    if (status === 'failed') {
      return this.updateManager.errorMessage() ?? 'No se pudo descargar la actualización. Forzá una recarga limpia cuando el estado sea seguro.';
    }

    if (status === 'unrecoverable') {
      return this.updateManager.errorMessage() ?? 'Hay un problema con la versión actual y es necesario recargar la aplicación.';
    }

    if (reasons.length === 0) {
      return 'La aplicación se actualizará automáticamente cuando no haya trabajo sensible en curso.';
    }

    if (reasons.includes(UpdateBanner.CHECKIN_BLOCKING_REASON)) {
      return `No se puede actualizar ahora porque ${this.describeBlockingReasons(reasons)}. Se aplicará al cerrar la sesión o finalizar el proceso.`;
    }

    return `No se puede actualizar ahora porque ${this.describeBlockingReasons(reasons)}. La aplicación se actualizará automáticamente cuando el estado sea seguro.`;
  });
  protected readonly primaryLabel = computed(() => {
    switch (this.updateManager.status()) {
      case 'failed':
        return 'Forzar actualización';
      case 'unrecoverable':
        return 'Recargar ahora';
      default:
        return 'Actualizar ahora';
    }
  });

  protected applyUpdate(): void {
    void this.updateManager.applyUpdate();
  }

  protected dismiss(): void {
    this.updateManager.dismiss();
  }

  private describeBlockingReasons(reasons: UpdateBlockerReason[]): string {
    if (reasons.length === 1) {
      return UpdateBanner.BLOCKING_REASON_LABELS[reasons[0]];
    }

    const labels = reasons.map(reason => UpdateBanner.BLOCKING_REASON_LABELS[reason]);
    const lastLabel = labels.at(-1);
    return `${labels.slice(0, -1).join(', ')} y ${lastLabel}`;
  }
}
